// React glue for panelview.ts and pagedraft.ts: panel flags in the URL (history steps) and form drafts in sessionStorage.
import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { useSearchParams } from './liveparams.ts';
import { useModalSteps } from './urlmodal.ts';
import { clearForm, readForm, writeForm } from './pagedraft.ts';
import { flagOn, withFlag, withParam } from './panelview.ts';

/**
 * A panel flag in the URL: [open, set]. Opening it is a history step (Back closes it); closing it from inside goes back
 * (or replaces when it was linked to directly), see lib/urlmodal.ts.
 */
export function useUrlFlag(key: string): [boolean, (on: boolean) => void] {
  const [params] = useSearchParams();
  const win = useModalSteps(`${key}Opened`);
  const on = flagOn(params, key);
  const set = useCallback((next: boolean) => {
    if (next === on) return;
    if (next) win.open((p) => withFlag(p, key, true));
    else win.close((p) => withFlag(p, key, false));
  }, [key, on, win]);
  return [on, set];
}

/** One value in the URL, a panel's subject: [value, set]; setting one opens it as a history step, undefined closes it. */
export function useUrlParam(key: string): [string | undefined, (value: string | undefined) => void] {
  const [params] = useSearchParams();
  const win = useModalSteps(`${key}Opened`);
  const value = params.get(key) || undefined;
  const set = useCallback((next: string | undefined) => {
    if (next === value) return;
    if (!next) win.close((p) => withParam(p, key, undefined));
    else if (win.opened) win.step((p) => withParam(p, key, next));
    else win.open((p) => withParam(p, key, next));
  }, [key, value, win]);
  return [value, set];
}

/**
 * The typed, unsent fields of a form, kept in sessionStorage under `key` as they are typed. [fields, set, clear]:
 * `set` merges a change, `clear` forgets the draft (after a send or a cancel). Never put a secret in it.
 */
export function useFormDraft<T extends object>(key: string, initial: T): [T, (patch: Partial<T>) => void, () => void] {
  const first = useRef(initial);
  const [value, setValue] = useState<T>(() => readForm(key, initial));
  const current = useRef(value);
  const set = useCallback((patch: Partial<T>) => {
    current.current = { ...current.current, ...patch };
    setValue(current.current);
    writeForm(key, current.current, first.current);
  }, [key]);
  const clear = useCallback(() => {
    current.current = first.current;
    setValue(first.current);
    clearForm(key);
  }, [key]);
  return [value, set, clear];
}

/**
 * A text input whose value lives in the URL: [text, setText]. The input is driven by local state (a controlled input
 * bound straight to the router drops characters while it navigates); every change is also `write`n to the URL, and the
 * text follows the URL only when the browser moved through history (Back, Forward).
 */
export function useSyncedText(urlValue: string, write: (text: string) => void): [string, (text: string) => void] {
  const location = useLocation();
  const navigation = useNavigationType();
  const [text, setText] = useState(urlValue);
  const seen = useRef(location.key);
  if (location.key !== seen.current) {
    seen.current = location.key;
    if (navigation === 'POP' && urlValue !== text) setText(urlValue);
  }
  const change = useCallback((t: string) => { setText(t); write(t); }, [write]);
  return [text, change];
}
